import * as PIXI from "pixi.js";
import DummyGameSnacks from "../lib/DummyGameSnacks";
import DeviceOrientation, { device_orientation_type } from "./DeviceOrientation";
import signals from "signals";
import { title } from "process";


export default class App
{
    // #region Attributes //

    // private:

    private _renderer: PIXI.Renderer = null;
    private _stage: PIXI.Container = null;
    private _mainDiv: HTMLDivElement = null;
    private _gameDiv: HTMLDivElement = null;
    private _privateGameShareUrlDiv: HTMLDivElement = null;

    private _root: PIXI.Container = null;

    private _safeArea: PIXI.Container = null;
    private _mask: PIXI.Graphics = null;
    private _deviceOrientation: DeviceOrientation = null;
    private _isTouchDevice: boolean = null;
    //private _viewportSizeViewer: ViewportSizeViewer = null;

    private _fpsText: PIXI.Text = null;
    private _dummyGameSnacks: DummyGameSnacks = null;

    // Signals.
    private _onOrientationChanged: signals.Signal = null;
    private _onCopyUrlBtnClick: signals.Signal = null;
    private _onPlayBtnClick: signals.Signal = null;

    // Binded functions.
    private _bfOnWindowResized: any = null;

    // Constants.
    private _kSafeAreaHeight: number = 1390;
    private _kSafeAreaWidth: number = 854;
    private _kHeight: number = 1136;
    private _kWidth: number = 640;
    private _kSafeAreaMarginX: number = ( this._kSafeAreaWidth - this._kWidth ) * 0.5;
    private _kSafeAreaMarginY: number = ( this._kSafeAreaHeight - this._kHeight ) * 0.5;

    // #endregion //


    // #region Properties //

    public get renderer(): PIXI.Renderer { return this._renderer; }
    public get stage(): PIXI.Container { return this._stage; }
    public get gameDiv(): HTMLDivElement { return this._gameDiv; }
    public get root(): PIXI.Container { return this._root; }
    public get orientation(): device_orientation_type { return this._deviceOrientation.orientation; }
    public get orientationStr(): string { return this._deviceOrientation.orientation == device_orientation_type.LANDSCAPE ? "landscape" : "portrait"; }
    public get isTouchDevice(): boolean { return this._isTouchDevice; }
    public get safeArea(): PIXI.Container { return this._safeArea; }
    public get width(): number { return this._deviceOrientation.orientation == device_orientation_type.PORTRAIT ? this._kWidth : this._kHeight; }
    public get height(): number { return this._deviceOrientation.orientation == device_orientation_type.PORTRAIT ? this._kHeight : this._kWidth; }
    public get safeAreaWidth(): number { return this._deviceOrientation.orientation == device_orientation_type.PORTRAIT ? this._kSafeAreaWidth : this._kSafeAreaHeight; }
    public get safeAreaHeight(): number { return this._deviceOrientation.orientation == device_orientation_type.PORTRAIT ? this._kSafeAreaHeight : this._kSafeAreaWidth; }
    public get safeAreaMarginX(): number { return this._deviceOrientation.orientation == device_orientation_type.PORTRAIT ? this._kSafeAreaMarginX : this._kSafeAreaMarginY; }
    public get safeAreaMarginY(): number { return this._deviceOrientation.orientation == device_orientation_type.PORTRAIT ? this._kSafeAreaMarginY : this._kSafeAreaMarginX; }
    
    // Signals.
    public get onOrientationChanged() { return this._onOrientationChanged; }
    public get onCopyUrlBtnClick() { return this._onCopyUrlBtnClick; }
    public get onPlayBtnClick() { return this._onPlayBtnClick; }

    // #endregion //


    // #region Methods //

    // public:

    public init(): void
    {
        this._onOrientationChanged = new signals.Signal();
        this._onCopyUrlBtnClick = new signals.Signal();
        this._onPlayBtnClick = new signals.Signal();

        this._isTouchDevice = false;

        // Setup body.
        document.body.style.margin = "0px";
        //document.body.style.overflow = "hidden";
        document.body.style.backgroundColor = "black";

        this._deviceOrientation = new DeviceOrientation();
        this._deviceOrientation.onChanged.add( this._onDeviceOrientation_Changed, this );

        this._renderer = new PIXI.Renderer( {
            width: document.documentElement.clientHeight * this._kSafeAreaWidth / this._kSafeAreaHeight,
            height: document.documentElement.clientHeight,
            backgroundAlpha: 1,
            autoDensity: true,
            antialias: true,
            preserveDrawingBuffer: false,
            resolution: Math.min( window.devicePixelRatio, 2 ),
            backgroundColor: 0x000000,
            clearBeforeRender: true } );
        
        let bodyDiv: HTMLElement = document.createElement( "div" );
        bodyDiv.style.position = "absolute";
        bodyDiv.style.width = "100%";
        bodyDiv.style.height = "100%";
        bodyDiv.style.overflow = "hidden";
        document.body.appendChild( bodyDiv );

        this._mainDiv = document.createElement( "div" );
        //this._mainDiv.style.width = "100%";
        this._mainDiv.style.display = "flex";
        this._mainDiv.style.justifyContent = "center";
        this._mainDiv.style.backgroundSize = "auto 100%";
        this._mainDiv.style.backgroundImage = "url(\"resources/img/background_pattern.png\")";
        this._mainDiv.style.backgroundRepeat = "repeat-x";
        bodyDiv.appendChild( this._mainDiv );
        //
        this._gameDiv = document.createElement( "div" );
        this._gameDiv.style.position = "relative";
        this._gameDiv.appendChild( this._renderer.view );
        this._privateGameShareUrlDiv = this._createPrivateGameShareUrlDiv();
        this.hidePrivateGameShareUrlDiv();
        this._gameDiv.appendChild( this._privateGameShareUrlDiv );
        this._mainDiv.appendChild( this._gameDiv );

        PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.LINEAR;

        this._stage = new PIXI.Container();
        this._stage.interactive = true;

        // Background.
        this._safeArea = new PIXI.Container();
        this._stage.addChild( this._safeArea );

        this._mask = new PIXI.Graphics();
        this._mask.beginFill( 0xffffff );
        this._mask.drawRect( -this.safeAreaWidth * 0.5, -this.safeAreaHeight * 0.5, this.safeAreaWidth, this.safeAreaHeight );
        this._mask.endFill();
        this._mask.isMask = true;
        this._stage.addChild( this._mask );

        // Game container.
        this._root = new PIXI.Container();
        this._root.mask = this._mask;
        this._stage.addChild( this._root );

        // TEST:
        // Player area.
        /*let g: PIXI.Graphics = new PIXI.Graphics();
        g.beginFill( 0xffffff, 0.5 );
        g.drawRect( 0, 0, this._kWidth, this._kHeight );
        g.endFill();
        this._root.addChild( g );*/

        /*this._viewportSizeViewer = new ViewportSizeViewer();
        this._app.stage.addChild( this._viewportSizeViewer );*/
        //

        this._onDeviceOrientation_Changed( this._deviceOrientation.orientation );

        // Listen to events.
        this._bfOnWindowResized = this.onWindow_Resized.bind( this );
        window.addEventListener( "resize", this._bfOnWindowResized );
        this._stage.once( "touchstart", () => { this._isTouchDevice = true; } );
    }

        private _createPrivateGameShareUrlDiv(): HTMLDivElement
        {
            let result: HTMLDivElement = document.createElement( "div" );
            result.style.width = "799px";
            result.style.position = "absolute";
            result.style.bottom = "28px";
            result.style.textAlign = "center";
            result.style.display = "flex";
            result.style.justifyContent = "center";
            result.style.alignItems = "center";
            result.style.flexDirection = "column";
            result.style.transformOrigin = "left bottom";

            let titleDiv: HTMLDivElement = document.createElement( "div" );
            titleDiv.id = "titleDiv";
            titleDiv.innerText = "Copy the link & share it with your friends";
            titleDiv.style.marginBottom = "30px";
            titleDiv.style.fontSize = "30px";
            titleDiv.style.color = "white";
            titleDiv.style.fontFamily = "asap_regular";
            result.appendChild( titleDiv );

            let contentDiv: HTMLDivElement = document.createElement( "div" );
            contentDiv.style.display = "flex";
            contentDiv.style.alignItems = "center";
            contentDiv.style.backgroundColor = "rgba(51, 61, 108, 0.9)";
            contentDiv.style.padding = "20px";
            contentDiv.style.borderRadius = "30px";
            contentDiv.style.outline = "none";
            contentDiv.style.position = "relative";
            result.appendChild( contentDiv );
            //
            let urlTextField: HTMLInputElement = document.createElement( "input" );
            urlTextField.type = "text";
            urlTextField.id = "shareUrlInput";
            urlTextField.style.width = "350px";
            urlTextField.style.height = "26px";
            urlTextField.style.marginRight = "120px";
            urlTextField.style.fontSize = "24px";
            urlTextField.style.fontFamily = "asap_bold";
            urlTextField.style.color = "white";
            urlTextField.style.background = "transparent";
            urlTextField.style.outline = "none";
            urlTextField.style.border = "none"
            urlTextField.style.whiteSpace = "nowrap";
            urlTextField.style.overflow = "hidden";
            urlTextField.style.textOverflow = "ellipsis";
            urlTextField.readOnly = true;
            contentDiv.appendChild( urlTextField );
            // 
            let copyUrlBtn: HTMLInputElement = document.createElement( "input" );
            copyUrlBtn.type = "image";
            copyUrlBtn.innerText = "Copy";
            copyUrlBtn.name = "copyUrlBtn";
            copyUrlBtn.src = "./resources/img/play_with_friends/button_copy@2x.png";
            copyUrlBtn.style.width = "120px";
            copyUrlBtn.style.position = "absolute";
            copyUrlBtn.style.right = "0px";
            contentDiv.appendChild( copyUrlBtn );
            copyUrlBtn.addEventListener( "mousedown", () => {
                copyUrlBtn.src = "./resources/img/play_with_friends/button_copy_pressed@2x.png";
            } );
            copyUrlBtn.addEventListener( "mouseup", () => {
                copyUrlBtn.src = "./resources/img/play_with_friends/button_copy@2x.png";
            } );
            copyUrlBtn.addEventListener( "mouseleave", () => {
                copyUrlBtn.src = "./resources/img/play_with_friends/button_copy@2x.png";
            } );
            copyUrlBtn.addEventListener( "click", () => { this._onCopyUrlBtnClick.dispatch(); } );

            let playDiv: HTMLDivElement = document.createElement( "div" );
            playDiv.id = "playDiv";
            playDiv.style.marginTop = "20px";
            playDiv.style.display = "none";
            result.appendChild( playDiv );
            //
            let playBtn: HTMLInputElement = document.createElement( "input" );
            playBtn.type = "image";
            playBtn.innerText = "";
            playBtn.name = "playBtn";
            playBtn.src = "./resources/img/play_with_friends/button_play@2x.png";
            playBtn.style.width = "260px";
            playDiv.appendChild( playBtn );
            /*playBtn.addEventListener( "mousedown", () => {
                playBtn.src = "./resources/img/play_with_friends/button_copy_pressed@2x.png";
            } );
            playBtn.addEventListener( "mouseup", () => {
                playBtn.src = "./resources/img/play_with_friends/button_copy@2x.png";
            } );
            playBtn.addEventListener( "mouseleave", () => {
                playBtn.src = "./resources/img/play_with_friends/button_copy@2x.png";
            } );*/
            playBtn.addEventListener( "click", () => { this._onPlayBtnClick.dispatch(); } );

            return result;
        }

        _onDeviceOrientation_Changed( orientation: device_orientation_type ): void
        {
            //console.log( "orientation=" + orientation );

            let bgPatternPrefix: string = "";
            if ( orientation == device_orientation_type.LANDSCAPE )
            {
                bgPatternPrefix = "_landscape";
            }
            this._mainDiv.style.backgroundImage = "url(\"resources/img/background_pattern" + bgPatternPrefix + ".png\")";

            this._mask.clear();
            this._mask.beginFill( 0xffffff );
            this._mask.drawRect( -this.safeAreaWidth * 0.5, -this.safeAreaHeight * 0.5, this.safeAreaWidth, this.safeAreaHeight );
            this._mask.endFill();

            this._renderer.resize( 
                document.documentElement.clientHeight * this.safeAreaWidth / this.safeAreaHeight,
                document.documentElement.clientHeight );
            this.onWindow_Resized();

            this._onOrientationChanged.dispatch( orientation );
        }    

    public end(): void
    {
        // Cleanup events.
        window.removeEventListener( "resize", this._bfOnWindowResized );
        this._bfOnWindowResized = null;

        this._onOrientationChanged.dispose();
        this._onOrientationChanged = null;

        this._onCopyUrlBtnClick.dispose();
        this._onCopyUrlBtnClick = null;

        this._onPlayBtnClick.dispose();
        this._onPlayBtnClick = null;

        this._deviceOrientation.onChanged.dispose();
        this._deviceOrientation = null;

        //this._viewportSizeViewer = null;
        this._root = null;

        this._stage.destroy( { children: true, texture: true, baseTexture: true } );
        this._stage = null;
        
        this._renderer.destroy( true );
        this._renderer = null;
    }

    public render(): void
    {
        this._renderer.render( this._stage );
    }

    public printFPS( fps: number ): void
    {
        if ( !this._fpsText )
        {
            this._fpsText = new PIXI.Text( "", { fill: "white", stroke: "black", strokeThickness: 4 } );
            this._fpsText.position.set( 5 );
            this._stage.addChild( this._fpsText );
        }
        this._fpsText.text = Math.round( fps ).toString() + "fps";
    }

    public showPrivateGameShareUrlDiv( url: string ): void
    {
        this._privateGameShareUrlDiv.style.display = "flex";
        ( this._privateGameShareUrlDiv.querySelector( "#shareUrlInput" ) as HTMLInputElement ).value = url;
    }

    public hidePrivateGameShareUrlDiv(): void
    {
        this._privateGameShareUrlDiv.style.display = "none";
    }

    public showPrivateGamePlayButton(): void
    {
        this._privateGameShareUrlDiv.style.bottom = "10px";
        ( this._privateGameShareUrlDiv.querySelector( "#titleDiv" ) as HTMLDivElement ).style.display = "none";
        ( this._privateGameShareUrlDiv.querySelector( "#playDiv" ) as HTMLDivElement ).style.display = "";
    }

    public hidePrivateGamePlayButton(): void
    {
        this._privateGameShareUrlDiv.style.bottom = "28px";
        ( this._privateGameShareUrlDiv.querySelector( "#titleDiv" ) as HTMLDivElement ).style.display = "";
        ( this._privateGameShareUrlDiv.querySelector( "#playDiv" ) as HTMLDivElement ).style.display = "none";
    }

    // #endregion //


    // #region Callbacks //

    private onWindow_Resized(): void
    {
        let rendererH: number = document.documentElement.clientHeight;
        let rendererW: number = rendererH * this.safeAreaWidth / this.height;
        if ( rendererW > document.documentElement.clientWidth )
        {
            rendererW = document.documentElement.clientWidth;
        }

        this._renderer.resize( rendererW, rendererH );

        this.resizeGame( rendererW, rendererH );

        const scaleX = this._privateGameShareUrlDiv.parentElement.clientWidth / 800;   // base width
        const scaleY = this._privateGameShareUrlDiv.parentElement.clientWidth / 400;  // base height
        const scale = Math.min(scaleX, scaleY);       // keep proportions
        this._privateGameShareUrlDiv.style.transform = `scale(${scale})`;

        /*this._viewportSizeViewer.updateText( document.documentElement.clientWidth, document.documentElement.clientHeight );
        this._viewportSizeViewer.position.set( 10, document.documentElement.clientHeight - this._viewportSizeViewer.height - 10 );*/
    }

        private resizeGame( rendererW: number, rendererH: number ): void
        {
            let scale: number = rendererW / this.width;  
            if ( this.height * scale > rendererH )
            {
                scale = rendererH / this.height;  
            }

            this._root.scale.set( scale );  
            this._root.x = ( rendererW - this.width * scale ) * 0.5;
            this._root.y = ( rendererH - this.height * scale ) * 0.5;

            if ( this._dummyGameSnacks )
            {
                this._dummyGameSnacks.scale.set( scale );
                this._dummyGameSnacks.position.copyFrom( this._root.position );
            }

            this._safeArea.position.set( rendererW * 0.5, rendererH * 0.5 );
            this._safeArea.scale.set( scale );

            this._mask.position.set( rendererW * 0.5, rendererH * 0.5 );
            this._mask.scale.set( scale );
        }

    public onDummyGameSnacks( dgs: DummyGameSnacks ): void
    {
        this._dummyGameSnacks = dgs
        this._dummyGameSnacks.mask = this._mask;
        this._dummyGameSnacks.scale.set( this._root.scale.x );
        this._dummyGameSnacks.position.copyFrom( this._root.position );
        this._stage.addChild( this._dummyGameSnacks );
    }

    // #endregion //
}